import { uiPaper } from "shared/ui/uiSurface"
import { Add, DeliveryDining, Search, SearchOff } from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { CourierItem, useGetCouriersQuery, useToggleCourierMutation } from "../../apis/appApi"
import { CouriersGrid } from "./CouriersGrid"
import { CourierDetailDrawer } from "./CourierDetailDrawer"
import { CourierFormDialog } from "./CourierFormDialog"

export const CouriersPage: FC = () => {
  const { t } = useTranslation()

  const [searchInput, setSearchInput] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [showDisabled, setShowDisabled] = useState(false)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [sortField, setSortField] = useState("id")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc")
  const [selectedCourierId, setSelectedCourierId] = useState<number | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editCourierId, setEditCourierId] = useState<number | null>(null)
  const [toggleTarget, setToggleTarget] = useState<CourierItem | null>(null)

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim()
      setDebouncedSearch(trimmed.length >= 3 ? trimmed : "")
      setPaginationModel((m) => ({ ...m, page: 0 }))
    }, 300)
  }, [])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const [toggleCourier] = useToggleCourierMutation()

  const { data: response, isLoading } = useGetCouriersQuery({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    search: debouncedSearch || undefined,
    sortField,
    sortOrder,
    showDisabled,
  })

  const sortModel: GridSortModel = sortField
    ? [{ field: sortField, sort: sortOrder }]
    : []

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length > 0) {
      setSortField(model[0].field)
      setSortOrder(model[0].sort ?? "asc")
    } else {
      setSortField("id")
      setSortOrder("desc")
    }
    setPaginationModel((m) => ({ ...m, page: 0 }))
  }, [])

  const rows = response?.data ?? []
  const rowCount = response?.total ?? 0

  const selectedCourier = rows.find((r) => r.id === selectedCourierId) ?? null

  const handleRowClick = useCallback((id: number) => {
    setSelectedCourierId(id)
  }, [])

  const handleDrawerClose = useCallback(() => {
    setSelectedCourierId(null)
  }, [])

  const handleEdit = useCallback((courier: CourierItem) => {
    setEditCourierId(courier.id)
    setSelectedCourierId(null)
  }, [])

  const handleToggleRequest = useCallback((courier: CourierItem) => {
    setToggleTarget(courier)
  }, [])

  const handleToggleConfirm = useCallback(async () => {
    if (!toggleTarget) return
    try {
      await toggleCourier(toggleTarget.id).unwrap()
    } catch (error) {
      console.error("Failed to toggle courier:", error)
    }
    setToggleTarget(null)
  }, [toggleTarget, toggleCourier])

  const handleCreate = useCallback(() => {
    setCreateDialogOpen(true)
  }, [])

  const handleCreateClose = useCallback(() => {
    setCreateDialogOpen(false)
  }, [])

  const handleEditClose = useCallback(() => {
    setEditCourierId(null)
  }, [])

  return (
    <Box
      data-testid="couriers-page"
      sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
    >
      {/* Page header */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "flex-start" }}
          spacing={1.5}
        >
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography variant="h4" fontWeight={700}>
                {t("couriers.title")}
              </Typography>
              {!isLoading && (
                <Typography variant="body2" color="text.secondary">
                  {t("couriers.couriersCount", { count: rowCount })}
                </Typography>
              )}
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("couriers.subtitle")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={handleCreate}
            data-testid="couriers-create-btn"
            sx={{ flexShrink: 0 }}
          >
            {t("couriers.newCourier")}
          </Button>
        </Stack>
      </Box>

      {/* Filters bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        sx={{ px: { xs: 2, md: 3 }, pb: 2 }}
      >
        <TextField
          size="small"
          placeholder={t("couriers.search")}
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          data-testid="couriers-search-input"
          sx={{ width: { xs: "100%", md: 280 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showDisabled}
              onChange={(e) => {
                setShowDisabled(e.target.checked)
                setPaginationModel((m) => ({ ...m, page: 0 }))
              }}
              data-testid="couriers-show-disabled-switch"
            />
          }
          label={t("couriers.showDisabled")}
        />
      </Stack>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "hidden", px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            {rowCount === 0 && !debouncedSearch && !showDisabled ? (
              <Stack
                data-testid="couriers-empty-state"
                alignItems="center"
                spacing={2}
                sx={{ maxWidth: 360 }}
              >
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DeliveryDining sx={{ fontSize: 36, color: "grey.400" }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {t("couriers.empty.title")}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t("couriers.empty.description")}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleCreate}
                >
                  {t("couriers.newCourier")}
                </Button>
              </Stack>
            ) : (
              <Stack
                data-testid="couriers-empty-filter"
                alignItems="center"
                spacing={2}
                sx={{ maxWidth: 360 }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SearchOff sx={{ fontSize: 32, color: "grey.400" }} />
                </Box>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {t("couriers.empty.filtered")}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {t("couriers.empty.filteredDescription")}
                </Typography>
              </Stack>
            )}
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 2,
            }}
          >
            <CouriersGrid
              rows={rows}
              loading={isLoading}
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              onRowClick={handleRowClick}
              onEdit={handleEdit}
              onToggle={handleToggleRequest}
            />
          </Paper>
        )}
      </Box>

      {/* Detail drawer */}
      <CourierDetailDrawer
        courier={selectedCourier}
        open={!!selectedCourierId}
        onClose={handleDrawerClose}
        onEdit={handleEdit}
        onToggle={handleToggleRequest}
      />

      {/* Form dialog (create) */}
      <CourierFormDialog
        open={createDialogOpen}
        courierId={null}
        onClose={handleCreateClose}
      />

      {/* Form dialog (edit) */}
      <CourierFormDialog
        courierId={editCourierId}
        open={!!editCourierId}
        onClose={handleEditClose}
      />

      {/* Toggle confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-127") }}
        open={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        maxWidth="xs"
        data-testid="courier-toggle-dialog"
      >
        <DialogTitle>
          {toggleTarget?.enabled ? t("couriers.toggle.disableTitle") : t("couriers.toggle.enableTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {toggleTarget?.enabled
              ? t("couriers.toggle.disableMessage", { name: `${toggleTarget?.firstName} ${toggleTarget?.lastName}` })
              : t("couriers.toggle.enableMessage", { name: `${toggleTarget?.firstName} ${toggleTarget?.lastName}` })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleTarget(null)}>{t("cancel")}</Button>
          <Button
            onClick={handleToggleConfirm}
            variant="contained"
            color={toggleTarget?.enabled ? "warning" : "success"}
          >
            {toggleTarget?.enabled ? t("couriers.drawer.disable") : t("couriers.drawer.enable")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
